// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

/**
 * Permission codes that mean the same thing.
 *
 * The catalogue carried every booking/product/report/role/tenant/user
 * permission twice — `booking.approve` and `bookings.approve`, `booking.read`
 * and `bookings.view` — and a role could hold either. The API has always
 * accepted both spellings (`permission_checker._equivalent_permission_codes`);
 * this app compared codes exactly, so a role granted the other spelling lost
 * the button. Same rules as the API: singular/plural resource, read/view,
 * update/write — plus the two product codes that were renamed rather than
 * pluralised.
 */

const RENAMED = {
  "product.inventory": ["products.inventory.manage"],
  "products.inventory.manage": ["product.inventory"],
  "product.pricing": ["products.pricing.manage"],
  "products.pricing.manage": ["product.pricing"],
};

const NO_PLURAL_STRIP = ["settings", "permissions"];

export function equivalentPermissionCodes(code) {
  const raw = String(code || "").trim();
  const dot = raw.indexOf(".");
  if (dot <= 0) return raw ? [raw] : [];
  const resource = raw.slice(0, dot);
  const action = raw.slice(dot + 1);

  const resources = new Set([resource]);
  if (
    resource.endsWith("s") &&
    !resource.endsWith("ss") &&
    resource.length > 1 &&
    !NO_PLURAL_STRIP.some((suffix) => resource.endsWith(suffix))
  ) {
    resources.add(resource.slice(0, -1));
  } else if (!resource.endsWith("s")) {
    resources.add(`${resource}s`);
  }

  const actions = new Set([action]);
  if (action === "read" || action === "view") {
    actions.add("read");
    actions.add("view");
  } else if (action === "update" || action === "write") {
    actions.add("update");
    actions.add("write");
  }

  const out = new Set();
  for (const res of resources) {
    for (const act of actions) out.add(`${res}.${act}`);
  }
  for (const alias of RENAMED[raw] || []) out.add(alias);
  return [...out];
}

/** True if `granted` holds `code` in any of its spellings. */
export function permissionGranted(granted, code) {
  if (!Array.isArray(granted)) return false;
  const set = new Set(granted);
  return equivalentPermissionCodes(code).some((c) => set.has(c));
}
